import React, { useEffect, useState } from "react";
import styles from "./styles.module.scss";

import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import Stack from "@mui/material/Stack";

import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, Controller } from "react-hook-form";
import { toast } from "react-toastify";
import closeIconModal from "./../../../../../assets/_CloseIconModal_.svg";
import { addUserToGroup, getAllUsers, getAllUsersByGroupId } from "../../../../../services/api/user/UserService";
import { IUserItem } from "../../../../../@types/user.type";
import { IKeyText } from "../../../../../@types/key-value.type";

const createUserSchema = z.object({
  users: z.array(z.object({
    key: z.string(),
    text: z.string(),
  })).nonempty("Selecione pelo menos um membro."),
});

interface CreateUserModalProps {
  open: boolean;
  onClose: () => void;
  groupId: string;
  reload: boolean;
}
const filter = createFilterOptions();

export const CreateUserModal: React.FC<CreateUserModalProps> = ({
  open,
  onClose,
  groupId,
  reload
}) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<{ users: { key: string; text: string }[] }>({
    resolver: zodResolver(createUserSchema),
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [usersList, setUsersList] = useState<IKeyText[]>([]);
  const [reloadModal, setReloadModal] = useState(true);

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [allUsersResponse, groupUsersResponse] = await Promise.all([
          getAllUsers(),
          getAllUsersByGroupId(groupId),
        ]);
  
        const groupUserIds = new Set(groupUsersResponse.result.map((user: IUserItem) => user.id));
  
        const options = allUsersResponse.result
          .filter((user: IUserItem) => !groupUserIds.has(user.id))
          .map((user: IUserItem) => ({
            key: user.id,
            text: user.email,
          }));

        setUsersList(options as IKeyText[]);
      } catch (error) {
        console.log(error);
      }
    };
    loadOptions();
  }, [reload, reloadModal]);
  
  const onSubmit = async (data: { users: { key: string; text: string }[] }) => {
    try {
      setLoading(true);

      const promises = data.users.map((user) => addUserToGroup(user.key, groupId));

      const responses = await Promise.allSettled(promises);

      const successfulAdds = responses.filter(
        (res) => res.status === "fulfilled"
      ).length;

      if (successfulAdds > 0) {
        toast.success(`${successfulAdds} membro(s) adicionado(s) com sucesso!`);
      }

      reset();
      onClose();
      setReloadModal((prev) => !prev);
    } catch (error) {
      toast.error("Erro ao adicionar os membros ao grupo. Tente novamente!");
      console.log(error)
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box className={styles.modalBox}>
        <div className={styles.modalTitle}>
          <h3>Adicionar Membros no Grupo</h3>
          <Button onClick={handleClose} className={styles.iconButton}>
            {closeIconModal && <img src={closeIconModal} />}
          </Button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name="users"
            control={control}
            defaultValue={[]}
            render={({ field }) => (
              <Autocomplete
                multiple
                options={usersList}
                getOptionLabel={(option) => option.text}
                isOptionEqualToValue={(option, value) => option.key === value.key}
                noOptionsText="Sem opções"
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    {option.text}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Selecione os Membros"
                    placeholder="Busque os membros aqui..."
                    error={!!errors.users}
                    helperText={errors.users?.message}
                  />
                )}
                onChange={(_, value) => field.onChange(value)}
                value={field.value}
              />
            )}
          />

          <Stack direction="row" spacing={2} className={styles.stackButton}>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={loading}
              className={styles.btnCreate}
            >
              Adicionar
            </Button>
          </Stack>
        </form>
      </Box>
    </Modal>
  );
};
  
